import React, { useState } from 'react';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';
import PptxGenJS from 'pptxgenjs';
import styles from './PptxPreview.module.css';

const parseMarkdownToSlides = (markdown) => {
    // Existing parseMarkdownToSlides function remains unchanged
    const slides = [];
    let currentSlide = { title: '', content: [], image: null, links: [] };

    const sections = markdown.split(/(?=## )/);

    for (let section of sections) {
        if (!section.trim()) continue;

        const lines = section.split('\n');
        currentSlide = { title: '', content: [], image: null, links: [] };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (line.startsWith('## ')) {
                currentSlide.title = line.replace(/^## /, '');
                continue;
            }

            const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/);
            if (imageMatch) {
                currentSlide.image = {
                    url: imageMatch[2],
                    alt: imageMatch[1]
                };
                continue;
            }

            const linkMatch = line.match(/\[(.*?)\]\((.*?)\)/);
            if (linkMatch && !imageMatch) {
                currentSlide.links.push(linkMatch[2]);
                continue;
            }

            if (line && !line.startsWith('#')) {
                const cleanLine = line
                    // eslint-disable-next-line
                    .replace(/^[\*\-]\s+/, '')
                    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
                    .trim();
                if (cleanLine) {
                    currentSlide.content.push(cleanLine);
                }
            }
        }

        if (currentSlide.title || currentSlide.content.length > 0) {
            slides.push(currentSlide);
        }
    }

    return slides;
};

const PptxPreview = ({ markdown, title, author }) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const slides = parseMarkdownToSlides(markdown);

    // Existing helper functions remain unchanged
    const getSlideTitle = () => {
        const safeTitle = (title || 'Presentation').replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const authorName = author?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'anonymous';
        return `${safeTitle}_by_${authorName}_on_topspeaks.com`;
    };

    const getBase64FromUrl = async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error fetching image:', error);
            return null;
        }
    };

    // Existing PPTX generation functions remain unchanged
    const addAttributionSlide = (pptx, isFirstSlide = false) => {
        const slide = pptx.addSlide();

        if (isFirstSlide && author) {
            slide.addText([{
                text: 'Author: ',
                options: { fontSize: 24, bold: true, color: '000000' }
            }, {
                text: author,
                options: { fontSize: 24, color: '000000' }
            }], {
                x: 0.5,
                y: 2,
                w: '90%',
                align: 'center'
            });
        } else {
            slide.addText('Created with TopSpeaks', {
                x: 0.5,
                y: 2,
                w: '90%',
                fontSize: 28,
                bold: true,
                color: '000000',
                align: 'center'
            });

            slide.addText('Visit us at:', {
                x: 0.5,
                y: 3,
                w: '90%',
                fontSize: 20,
                color: '000000',
                align: 'center'
            });

            slide.addText('https://topspeaks.com', {
                x: 0.5,
                y: 3.5,
                w: '90%',
                fontSize: 20,
                color: '0000FF',
                align: 'center'
            });
        }
    };

    const generatePptx = async () => {
        try {
            setIsDownloading(true);
            const pptx = new PptxGenJS();

            addAttributionSlide(pptx, true);

            pptx.defineSlideMaster({
                title: 'MASTER_SLIDE',
                background: { color: 'FFFFFF' },
                margin: [0.5, 0.5, 0.5, 0.5],
                objects: []
            });

            for (const slide of slides) {
                const pptxSlide = pptx.addSlide({
                    masterName: 'MASTER_SLIDE'
                });

                if (slide.image?.url) {
                    try {
                        const base64Image = await getBase64FromUrl(slide.image.url);
                        if (base64Image) {
                            pptxSlide.background = { data: base64Image };
                            pptxSlide.addShape(pptx.ShapeType.rect, {
                                x: 0,
                                y: 0,
                                w: '100%',
                                h: '100%',
                                fill: {
                                    color: '000000',
                                    transparency: 40
                                },
                                line: { color: 'transparent' }
                            });
                        } else {
                            pptxSlide.background = { color: '000000' };
                        }
                    } catch (error) {
                        console.error('Error setting background image:', error);
                        pptxSlide.background = { color: '000000' };
                    }
                } else {
                    pptxSlide.background = { color: 'FFFFFF' };
                }

                if (slide.title) {
                    pptxSlide.addText(slide.title, {
                        x: 0.5,
                        y: 0.5,
                        w: 12,
                        h: 1.5,
                        bold: true,
                        fontSize: 32,
                        color: slide.image?.url ? 'FFFFFF' : '000000',
                        fontFace: 'Arial',
                        wrap: true,
                        fit: 'shrink'
                    });
                }

                if (slide.content.length > 0) {
                    const contentY = slide.title ? 2 : 0.5;
                    const maxContentHeight = 5;
                    const heightPerPoint = Math.min(0.6, maxContentHeight / slide.content.length);

                    slide.content.forEach((point, idx) => {
                        pptxSlide.addText(point, {
                            x: 0.5,
                            y: contentY + (idx * heightPerPoint),
                            w: 12,
                            h: heightPerPoint,
                            bullet: true,
                            color: slide.image?.url ? 'FFFFFF' : '000000',
                            fontSize: Math.max(14, Math.min(18, 24 - slide.content.length)),
                            fontFace: 'Arial',
                            wrap: true,
                            fit: 'shrink',
                            breakLine: true
                        });
                    });
                }

                if (slide.links.length > 0) {
                    const contentSpace = slide.content.length * 0.4;
                    const linksY = Math.min(5, contentSpace + (slide.title ? 2.5 : 1));

                    slide.links.forEach((link, idx) => {
                        pptxSlide.addText(link, {
                            x: 0.5,
                            y: linksY + 0.7 + (idx * 0.4),
                            w: 12,
                            h: 0.4,
                            fontSize: 14,
                            color: slide.image?.url ? '99CCFF' : '0066CC',
                            fontFace: 'Arial',
                            wrap: true,
                            fit: 'shrink'
                        });
                    });
                }
            }

            addAttributionSlide(pptx, false);

            const fileName = `${getSlideTitle()}.pptx`;
            await pptx.writeFile({ fileName });
        } catch (error) {
            console.error('Error generating PPTX:', error);
            alert('Failed to generate presentation. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.previewCard}>
                    <div className={styles.header}>
                        <div className={styles.navigation}>
                            <button
                                onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                                disabled={currentSlide === 0}
                                className={styles.navButton}
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <span className={styles.slideCount}>
                                Slide {currentSlide + 1} of {slides.length}
                            </span>
                            <button
                                onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                                disabled={currentSlide === slides.length - 1}
                                className={styles.navButton}
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                        <button
                            className={styles.downloadButton}
                            onClick={generatePptx}
                            disabled={isDownloading}
                        >
                            {isDownloading ? (
                                'Generating...'
                            ) : (
                                <>
                                    <Download size={18} />
                                    Export to PowerPoint
                                </>
                            )}
                        </button>
                    </div>

                    <div
                        className={styles.slide}
                        style={{
                            backgroundImage: slides[currentSlide]?.image
                                ? `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${slides[currentSlide].image.url})`
                                : undefined
                        }}
                    >
                        {slides[currentSlide]?.title && (
                            <h2 className={`${styles.slideTitle} ${slides[currentSlide].image ? styles.lightText : ''}`}>
                                {slides[currentSlide].title}
                            </h2>
                        )}
                        <div className={`${styles.slideContent} ${slides[currentSlide]?.image ? styles.lightText : ''}`}>
                            {slides[currentSlide]?.content.map((line, i) => (
                                <p key={i} className={styles.bulletPoint}>{line}</p>
                            ))}
                            {slides[currentSlide]?.links.length > 0 && (
                                <div className={styles.linkSection}>
                                    {slides[currentSlide].links.map((link, i) => (
                                        <a
                                            key={i}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.link}
                                        >
                                            {link}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PptxPreview;